import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Bridges the Kraken Cart drawer app to the theme's own cart events.
 *
 * Why this exists: the cart drawer is the Kraken Cart app embed, not the theme drawer. Kraken mutates
 * the cart through the Storefront API (`/api/<version>/graphql.json`), never through the AJAX cart
 * routes, and it only emits its own `krakenCart.publicEvents.*` events on `window`. The theme's
 * `cart:update` event is therefore never dispatched, so `<cart-icon>` (assets/cart-icon.js) only ever
 * shows the Liquid-rendered `cart.item_count` from the last full page load.
 *
 * Kraken does ship a Horizon cart-icon adapter, but it is gated on `Shopify.theme.theme_store_id === 2481`
 * and this theme is a custom fork (`theme_store_id === null`), so that adapter never runs for us.
 *
 * This module translates the Kraken events into `CartUpdateEvent` on `document` and exposes a small
 * shared cart-fetch helper so other modules (assets/bundle-gift-reconciler.js) do not each hit
 * `/cart.js` for the same change.
 *
 * @typedef {() => void} CartChangeHandler
 */

/** Event names emitted by the Kraken Cart app embed. These are undocumented — see the boot check below. */
export const KrakenCartEvents = Object.freeze({
  added: 'krakenCart.publicEvents.addedToCart',
  updated: 'krakenCart.publicEvents.updateCart',
  removed: 'krakenCart.publicEvents.removedFromCart',
});

/**
 * Source tag put on the dispatched `CartUpdateEvent`.
 * It must NOT be `product-form-component`: `cart-icon.js` treats that source as a delta and does
 * `currentCartCount + itemCount`, while we always publish an absolute count.
 */
const CART_SOURCE = 'kraken-cart';

/** How long to wait for the Kraken app embed before warning that the integration is broken. */
const KRAKEN_BOOT_TIMEOUT = 5000;

/** How long a cart mutation may go without a matching `updateCart` event before we warn. */
const KRAKEN_EVENT_TIMEOUT = 4000;

/**
 * @type {number | null} Last published item count, used to suppress duplicate dispatches.
 * Seeded from the server-rendered count so the initial sync stays silent when nothing changed —
 * `cart:update` also makes the /cart page re-render its items section, which is wasted work on load.
 */
let lastPublishedCount =
  typeof Theme !== 'undefined' && typeof Theme.cart?.item_count === 'number' ? Theme.cart.item_count : null;

/** @type {Promise<Record<string, any> | null> | null} In-flight `/cart.js` request, shared by all callers. */
let inFlightCart = null;

/** @type {Promise<Record<string, any> | null> | null} Most recent `/cart.js` request, settled or not. */
let lastRequest = null;

/** When `lastRequest` was issued, on the `performance.now()` clock. */
let lastRequestIssuedAt = 0;

/** When the cart last changed, as far as this bridge knows. Same clock as `lastRequestIssuedAt`. */
let lastChangeAt = 0;

/** @type {Set<CartChangeHandler>} */
const changeHandlers = new Set();

/** @returns {string} The locale-aware `/cart.js` URL. */
function cartUrl() {
  const base = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';

  return `${base.replace(/\/+$/, '')}.js`;
}

/**
 * Records that the cart changed. Every event path in this module calls it before it fetches, so a
 * `/cart.js` request issued afterwards is known to reflect that change. Exported for modules that
 * learn about cart changes through other channels (the theme's own `cart:update`).
 */
export function markCartChanged() {
  lastChangeAt = performance.now();
}

/**
 * Fetches the authoritative cart. Concurrent callers share a single request so a Kraken event storm
 * does not produce a burst of `/cart.js` round trips.
 *
 * @param {object} [options]
 * @param {boolean} [options.fresh] - Bypass the in-flight request and start a new one.
 * @param {boolean} [options.sinceLastChange] - Reuse the most recent request, settled or not, when
 *   it was issued after the last known cart change; otherwise start a new one. Lets a debounced
 *   subscriber read the cart the event handler already fetched instead of fetching it again.
 * @returns {Promise<Record<string, any> | null>} The cart, or null when the request failed.
 */
export function fetchCart({ fresh = false, sinceLastChange = false } = {}) {
  if (sinceLastChange) {
    if (lastRequest && lastRequestIssuedAt >= lastChangeAt) return lastRequest;
  } else if (inFlightCart && !fresh) {
    return inFlightCart;
  }

  const issuedAt = performance.now();
  const request = fetch(cartUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => {
      // Only clear the slot if a newer request has not already claimed it.
      if (inFlightCart === request) inFlightCart = null;
    });

  inFlightCart = request;
  lastRequest = request;
  lastRequestIssuedAt = issuedAt;

  return request;
}

/**
 * Publishes an absolute cart item count to the theme, which updates `<cart-icon>`.
 *
 * @param {number} itemCount - The absolute number of items in the cart.
 * @param {object} [options]
 * @param {boolean} [options.animate] - Whether the cart bubble should animate. False for the initial sync.
 */
export function publishCartCount(itemCount, { animate = true } = {}) {
  if (typeof itemCount !== 'number' || Number.isNaN(itemCount)) return;
  if (itemCount === lastPublishedCount) return;

  lastPublishedCount = itemCount;

  document.dispatchEvent(new CartUpdateEvent({}, CART_SOURCE, { itemCount, source: CART_SOURCE, animate }));
}

/**
 * Subscribes to "the Kraken cart changed" as a single normalised signal.
 *
 * Deliberately carries no payload: subscribers debounce, so a cart handed over here would already be
 * stale by the time they act on it. They re-read `fetchCart({ fresh: true })` instead.
 *
 * @param {CartChangeHandler} handler
 * @returns {() => void} Unsubscribe function.
 */
export function onKrakenCartChange(handler) {
  changeHandlers.add(handler);

  return () => changeHandlers.delete(handler);
}

function notifyChangeHandlers() {
  for (const handler of changeHandlers) {
    try {
      handler();
    } catch (error) {
      console.warn('[kraken-cart-bridge] cart change handler failed', error);
    }
  }
}

/**
 * Reads the authoritative count from `/cart.js` and publishes it.
 * Used for `addedToCart` / `removedFromCart`, which do not carry a cart total.
 *
 * @param {object} [options]
 * @param {boolean} [options.animate]
 */
async function syncFromCartJs({ animate = true } = {}) {
  const cart = await fetchCart();

  if (cart) publishCartCount(cart.item_count, { animate });

  notifyChangeHandlers();
}

/**
 * Both this bridge and the gift reconciler depend on undocumented third-party event names. If a
 * Kraken app update renames them, everything here silently stops working. These breadcrumbs make
 * that loud instead. `window.__krakenBridgeDiagnostics` is a plain array so an error tracker (or a
 * support session in the console) can read it without extra wiring.
 */
const diagnostics = (window.__krakenBridgeDiagnostics ??= []);

/** @type {ReturnType<typeof setTimeout> | undefined} */
let missingEventTimer;
let sawUpdateEvent = false;

/**
 * @param {string} code
 * @param {string} message
 */
function breadcrumb(code, message) {
  diagnostics.push({ code, message, at: Date.now() });
  console.warn(`[kraken-cart-bridge] ${message}`);
}

function watchForMissingUpdateEvent() {
  if (sawUpdateEvent) return;

  clearTimeout(missingEventTimer);
  missingEventTimer = setTimeout(() => {
    if (sawUpdateEvent) return;

    breadcrumb(
      'kraken-update-event-missing',
      `the cart changed but "${KrakenCartEvents.updated}" never fired — the Kraken event contract may have changed`
    );
  }, KRAKEN_EVENT_TIMEOUT);
}

// The theme's own cart paths (product forms, the /cart page) update `<cart-icon>` directly and never
// come through `publishCartCount`. Mirror what cart-icon.js renders so the duplicate guard above
// compares against the badge's real value — otherwise a later Kraken event carrying the same total
// is suppressed while the badge shows something else, or replays an animation the form already ran.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = /** @type {CartUpdateEvent} */ (event).detail?.data;

  if (!data || data.source === CART_SOURCE || typeof data.itemCount !== 'number') return;

  lastPublishedCount =
    data.source === 'product-form-component' ? (lastPublishedCount ?? 0) + data.itemCount : data.itemCount;
});

window.addEventListener(KrakenCartEvents.updated, (event) => {
  sawUpdateEvent = true;
  clearTimeout(missingEventTimer);
  markCartChanged();

  const totalQuantity = /** @type {CustomEvent} */ (event).detail?.cart?.totalQuantity;

  // Fast path: the event carries the total, so the badge updates without waiting on a round trip.
  if (typeof totalQuantity === 'number') {
    publishCartCount(totalQuantity);
    notifyChangeHandlers();

    return;
  }

  syncFromCartJs();
});

// `addedToCart` / `removedFromCart` carry only items/ids, so resolve the authoritative count.
window.addEventListener(KrakenCartEvents.added, () => {
  watchForMissingUpdateEvent();
  markCartChanged();
  syncFromCartJs();
});

window.addEventListener(KrakenCartEvents.removed, () => {
  watchForMissingUpdateEvent();
  markCartChanged();
  syncFromCartJs();
});

// Kraken may hydrate before this module runs, and the count in sessionStorage can be stale after a
// hard reload. Seed from the server without animating so no bubble animation plays on page load.
function boot() {
  markCartChanged();
  syncFromCartJs({ animate: false });

  setTimeout(() => {
    if (!window.KrakenCart) {
      breadcrumb('kraken-missing', 'window.KrakenCart is undefined — the cart drawer app embed did not load');
    }
  }, KRAKEN_BOOT_TIMEOUT);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
